import { cn } from "@/lib/utils";
import { GripVerticalIcon, Minimize2Icon } from "lucide-react";
import React from "react";

interface ResizableSidePanelProps extends React.HTMLAttributes<HTMLDivElement> {
  widthStorageKey: string;
  minWidth?: number;
  maxWidth?: number;
  defaultWidth?: number;
  onClose?: () => void;
  children: React.ReactNode;
}

export const ResizableSidePanel = React.forwardRef<
  HTMLDivElement,
  ResizableSidePanelProps
>(function ResizableSidePanel(
  {
    widthStorageKey,
    minWidth = 320,
    maxWidth = 800,
    defaultWidth = 380,
    onClose,
    className = "",
    children,
    ...rest
  },
  ref,
) {
  const [width, setWidth] = React.useState(() => {
    const saved = localStorage.getItem(widthStorageKey);
    return saved ? parseInt(saved, 10) : defaultWidth;
  });
  const [isResizing, setIsResizing] = React.useState(false);
  const widthRef = React.useRef(width);
  widthRef.current = width;

  React.useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const next = window.innerWidth - e.clientX;
      setWidth(Math.min(Math.max(next, minWidth), maxWidth));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      localStorage.setItem(widthStorageKey, widthRef.current.toString());
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, minWidth, maxWidth, widthStorageKey]);

  return (
    <div
      {...rest}
      ref={ref}
      style={{ width, ...(rest.style ?? {}) }}
      className={cn(
        "fixed right-0 top-0 z-50 flex h-screen flex-col border bg-card text-card-foreground",
        className,
      )}
    >
      <div
        className={cn(
          "absolute left-0 top-0 z-20 flex h-full w-3 cursor-ew-resize items-center justify-center hover:bg-primary/10",
          isResizing && "bg-primary/20",
        )}
        onMouseDown={(e) => {
          e.preventDefault();
          setIsResizing(true);
        }}
      >
        <GripVerticalIcon className="h-4 w-4 text-muted-foreground/50" />
      </div>
      {onClose && (
        <Minimize2Icon
          className="absolute right-4 top-4 z-10 h-4 w-4 cursor-pointer text-muted-foreground hover:text-foreground"
          onClick={onClose}
        />
      )}
      {children}
    </div>
  );
});
